import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { pole } from '../utils';
import { getTokenPayload } from '../auth';
import { STAV_TIKET_LABEL, STAV_TIKET_COLOR } from '../tiketyConst';
import SlaIndikator from './SlaIndikator';
import { inputStyle, selectStyle, btnPrimary, btnOutline, card, chyboveHlaseni, apiChyba, formatDatCas, useSirka } from '../ui';
import { DateInput } from './DateInput';
import { useNovaPrirazeni, odznakStyl } from '../novaPrirazeni';

const API = process.env.REACT_APP_API_URL || '/api';

const STAV_FILTR = [
  ['vychozi', 'Aktivní + uzavřené za 5 prac. dnů'],
  ['uzavreno', 'Uzavřené'],
  ['stornovano', 'Stornované'],
  ['vse', 'Vše'],
];

function StavOdznak({ t }) {
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      <span style={{ color: STAV_TIKET_COLOR[t.stav], fontWeight: 'bold' }}>{STAV_TIKET_LABEL[t.stav]}</span>
      {t.bude_vyjezd && <span title="Bude výjezd" style={{ marginLeft: '6px', fontSize: '12px', color: '#92400e', background: '#fef3c7', borderRadius: '10px', padding: '1px 7px' }}>bude výjezd</span>}
      {t.nepodepsano && <span style={{ marginLeft: '6px', fontSize: '12px', fontWeight: 'bold', color: '#fff', background: '#dc2626', borderRadius: '10px', padding: '1px 8px' }}>Nepodepsáno</span>}
      {t.nedoplneno && <span style={{ marginLeft: '6px', fontSize: '12px', color: '#9a3412', background: '#ffedd5', borderRadius: '10px', padding: '1px 7px' }}>Nedoplněno</span>}
    </span>
  );
}

// Přehled tiketů. režim 'technik' = záložky Moje tikety / Fronta; jinak plný přehled (dispečer, admin).
export default function PrehledTiketu({ setStranka, naviguj, rezim = 'vse', nadpis = 'Přehled tiketů' }) {
  const sirka = useSirka();
  const mobil = sirka < 760;
  const ja = getTokenPayload();
  const technik = rezim === 'technik';

  const [zalozka, setZalozka] = useState('moje');
  const [stav, setStav] = useState('vychozi');
  const [zakaznikId, setZakaznikId] = useState('');
  const [resitelId, setResitelId] = useState('');  // '' | 'fronta' | id
  const [od, setOd] = useState('');
  const [do_, setDo] = useState('');
  const [q, setQ] = useState('');
  const [qPouzit, setQPouzit] = useState('');
  const [zakaznici, setZakaznici] = useState([]);
  const [resitele, setResitele] = useState([]);
  const [tikety, setTikety] = useState(null);
  const [chyba, setChyba] = useState(null);
  const [nove, obnovNove] = useNovaPrirazeni(technik);

  useEffect(() => {
    if (technik) return;
    axios.get(`${API}/zakaznici`).then(r => setZakaznici(pole(r.data))).catch(() => {});
    axios.get(`${API}/resitele`).then(r => setResitele(pole(r.data))).catch(() => {});
  }, [technik]);

  useEffect(() => { const h = setTimeout(() => setQPouzit(q), 300); return () => clearTimeout(h); }, [q]);

  const nacti = useCallback(() => {
    const params = { stav };
    if (technik) {
      if (zalozka === 'fronta') params.fronta = true;
      else if (ja?.role !== 'technik') params.resitel_id = ja?.uid;   // dispečer · technik: backend jinak vrací všechny tikety
    } else {
      if (zakaznikId) params.zakaznik_id = zakaznikId;
      if (resitelId === 'fronta') params.fronta = true;
      else if (resitelId) params.resitel_id = resitelId;
    }
    if (od) params.od = od;
    if (do_) params.do = do_;
    if (qPouzit.trim()) params.q = qPouzit.trim();
    if (technik && zalozka === 'fronta') params.stav = 'aktivni';
    axios.get(`${API}/tikety`, { params })
      .then(r => { setTikety(pole(r.data)); setChyba(null); })
      .catch(e => { setTikety([]); setChyba(apiChyba(e, 'Nepodařilo se načíst tikety.')); });
  }, [stav, zakaznikId, resitelId, od, do_, qPouzit, technik, zalozka, ja?.role, ja?.uid]);

  useEffect(() => { nacti(); obnovNove(); }, [nacti, obnovNove]);

  const vzitSi = async (t, e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/tikety/${t.id}/priradit`, { resitel_id: ja.uid });
      naviguj('tiket-detail', t.id);   // tiket je teď v Moje tikety a rovnou se otevře jeho detail
    } catch (err) { setChyba(apiChyba(err)); nacti(); }
  };

  // Dispečer/admin přiřadí řešitele přímo z řádku/karty ve frontě (backend: POST /tikety/{id}/priradit)
  const priradit = async (t, resitelId) => {
    if (!resitelId) return;
    try { await axios.post(`${API}/tikety/${t.id}/priradit`, { resitel_id: Number(resitelId) }); nacti(); }
    catch (err) { setChyba(apiChyba(err)); nacti(); }
  };
  const vyberResitele = (t, styl) => !technik && !t.resitel && !sede(t) && (
    <select aria-label="Přiřadit řešitele" style={{ ...selectStyle, ...styl }} value="" onClick={e => e.stopPropagation()}
      onChange={e => priradit(t, e.target.value)}>
      <option value="">Přiřadit řešitele…</option>
      {resitele.map(r => <option key={r.id} value={r.id}>{r.jmeno}</option>)}
    </select>
  );

  const otevri = t => naviguj('tiket-detail', t.id);
  const sede = t => t.stav === 'uzavreno' || t.stav === 'stornovano';
  const pozadi = t => (sede(t) ? '#f3f4f6' : t.nove_prirazeni ? '#ecfdf5' : t.aktualizovano ? '#fffbeb' : '#fff');
  const znackaNove = t => (
    <>
      {t.nove_prirazeni && <span style={{ marginRight: '6px', fontSize: '12px', fontWeight: 'bold', color: '#fff', background: '#16a34a', borderRadius: '10px', padding: '1px 8px' }}>Nové</span>}
      {t.aktualizovano && <span title="Někdo přidal poznámku" style={{ marginRight: '6px', fontSize: '12px', fontWeight: 'bold', color: '#fff', background: '#d97706', borderRadius: '10px', padding: '1px 8px' }}>Aktualizováno</span>}
    </>
  );

  const filtry = (
    <div style={{ ...card, display: 'grid', gap: '8px', gridTemplateColumns: mobil ? '1fr' : 'repeat(4, 1fr)' }}>
      <input style={{ ...inputStyle, gridColumn: mobil ? 'auto' : 'span 2' }} placeholder="Hledat: OBJ, prodejna, popis…" value={q} onChange={e => setQ(e.target.value)} />
      {!(technik && zalozka === 'fronta') && (
        <select style={selectStyle} value={stav} onChange={e => setStav(e.target.value)}>
          {STAV_FILTR.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      )}
      {!technik && (
        <>
          <select style={selectStyle} value={zakaznikId} onChange={e => setZakaznikId(e.target.value)}>
            <option value="">Všichni zákazníci</option>
            {zakaznici.map(z => <option key={z.id} value={z.id}>{z.nazev}</option>)}
          </select>
          <select style={selectStyle} value={resitelId} onChange={e => setResitelId(e.target.value)}>
            <option value="">Všichni řešitelé</option>
            <option value="fronta">Fronta (bez řešitele)</option>
            {resitele.map(r => <option key={r.id} value={r.id}>{r.jmeno}</option>)}
          </select>
        </>
      )}
      <label style={{ fontSize: '12px', color: '#6b7280' }}>Přijato od
        <DateInput value={od} onChange={setOd} />
      </label>
      <label style={{ fontSize: '12px', color: '#6b7280' }}>Přijato do
        <DateInput value={do_} onChange={setDo} />
      </label>
    </div>
  );

  const seznam = tikety === null ? <p>Načítám…</p> : tikety.length === 0 ? (
    <p style={{ color: '#6b7280' }}>Žádné tikety neodpovídají filtru.</p>
  ) : mobil ? (
    tikety.map(t => (
      <div key={t.id} onClick={() => otevri(t)} style={{ ...card, cursor: 'pointer', opacity: sede(t) ? 0.6 : 1, background: pozadi(t), borderLeft: t.nove_prirazeni ? '4px solid #16a34a' : t.aktualizovano ? '4px solid #d97706' : card.borderLeft }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
          <span>{znackaNove(t)}<strong style={{ color: '#15803d' }}>OBJ {t.obj}</strong></span>
          <SlaIndikator sla={t.sla} />
        </div>
        <div style={{ margin: '4px 0' }}>{t.zakaznik_nazev} · {t.provozovna_nazev}{t.provozovna_mesto ? `, ${t.provozovna_mesto}` : ''}</div>
        <div style={{ fontSize: '13px', color: '#6b7280' }}>
          <StavOdznak t={t} /> · řešitel: {t.resitel?.jmeno || '—'} · založil: {t.zalozil.jmeno}
        </div>
        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>přijato {formatDatCas(t.prijato_at)} · č. {t.id}</div>
        {technik && zalozka === 'fronta' && (
          <button style={{ ...btnPrimary, width: '100%', marginTop: '10px' }} onClick={e => vzitSi(t, e)}>Vzít si</button>
        )}
        {vyberResitele(t, { marginTop: '10px' })}
      </div>
    ))
  ) : (
    <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
        <thead>
          <tr style={{ background: '#f9fafb', textAlign: 'left', color: '#6b7280' }}>
            {['OBJ', 'Č.', 'Zákazník', 'Prodejna', 'Stav', 'Řešitel', 'Založil', 'Přijato', 'SLA', ''].map(h => <th key={h} style={{ padding: '10px 8px' }}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {tikety.map(t => (
            <tr key={t.id} onClick={() => otevri(t)} style={{ cursor: 'pointer', borderTop: '1px solid #e5e7eb', opacity: sede(t) ? 0.6 : 1, background: pozadi(t) }}>
              <td style={{ padding: '10px 8px', fontWeight: 'bold', color: '#15803d' }}>{znackaNove(t)}{t.obj}</td>
              <td style={{ padding: '10px 8px', color: '#6b7280' }}>{t.id}</td>
              <td style={{ padding: '10px 8px' }}>{t.zakaznik_nazev}</td>
              <td style={{ padding: '10px 8px' }}>{t.provozovna_nazev}{t.provozovna_mesto ? `, ${t.provozovna_mesto}` : ''}</td>
              <td style={{ padding: '10px 8px' }}><StavOdznak t={t} /></td>
              <td style={{ padding: '10px 8px' }}>{t.resitel?.jmeno || '—'}</td>
              <td style={{ padding: '10px 8px' }}>{t.zalozil.jmeno}</td>
              <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>{formatDatCas(t.prijato_at)}</td>
              <td style={{ padding: '10px 8px' }}><SlaIndikator sla={t.sla} /></td>
              <td style={{ padding: '10px 8px' }}>
                {technik && zalozka === 'fronta' && <button style={{ ...btnPrimary, padding: '6px 12px', minHeight: 0 }} onClick={e => vzitSi(t, e)}>Vzít si</button>}
                {vyberResitele(t, { padding: '6px 8px', minHeight: 0, width: 'auto' })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      <button onClick={() => setStranka('rozcesti')} style={{ ...btnOutline, marginBottom: '14px' }}>← Zpět</button>
      <h2 style={{ margin: '0 0 12px', color: '#333' }}>{nadpis}</h2>
      {technik && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          {[['moje', 'Moje tikety'], ['fronta', 'Fronta']].map(([k, l]) => (
            <button key={k} onClick={() => setZalozka(k)} style={{ ...(zalozka === k ? btnPrimary : btnOutline), flex: 1 }}>
              {l}{k === 'moje' && nove > 0 && <span style={odznakStyl} title="Nově přiřazené, ještě neotevřené">{nove}</span>}
            </button>
          ))}
        </div>
      )}
      {filtry}
      {chyba && <div style={chyboveHlaseni}>{chyba}</div>}
      {seznam}
    </div>
  );
}
